import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, redirect, useRouter } from "@tanstack/react-router";
import { BrandWordmark } from "@manos/blocks";
import { Shell } from "./components/Shell";
import { ApiError, meQuery } from "./lib/api";
import { Login } from "./routes/Login";

/** A session that expired (or was revoked) mid-visit: any 401 sends the person to sign in again. */
function onApiError(err: unknown) {
  if (err instanceof ApiError && err.status === 401 && !location.pathname.startsWith("/login")) {
    queryClient.setQueryData(meQuery.queryKey, null);
    void router.navigate({ to: "/login", search: {} });
  }
}

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: true } },
  queryCache: new QueryCache({ onError: onApiError }),
  mutationCache: new MutationCache({ onError: onApiError }),
});

const root = createRootRouteWithContext<{ queryClient: QueryClient }>()({ component: Outlet, errorComponent: ServerError });

/** Shown when the API can't be reached, instead of pretending the person is signed out. */
function ServerError({ reset }: { reset: () => void }) {
  const r = useRouter();
  return (
    <div className="login">
      <div className="login-card">
        <div className="brand lg">
          <BrandWordmark />
          <span>Studio</span>
        </div>
        <p>Manos Studio can't reach its server right now. Your work is safe; nothing was lost.</p>
        <button
          type="button"
          className="btn primary block"
          onClick={() => {
            // A failed route check stays failed until the router re-runs it.
            void queryClient.resetQueries().then(() => r.invalidate());
            reset();
          }}
        >
          Try again
        </button>
      </div>
    </div>
  );
}

const login = createRoute({
  getParentRoute: () => root,
  path: "/login",
  validateSearch: (s: Record<string, unknown>): { error?: string } => (typeof s.error === "string" ? { error: s.error } : {}),
  component: Login,
});

/** Everything under here requires a signed-in user. */
const app = createRoute({
  getParentRoute: () => root,
  id: "_app",
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData(meQuery);
    if (!user) throw redirect({ to: "/login", search: {} });
    return { user };
  },
  component: Shell,
});

const parent = { getParentRoute: () => app };
// Each screen is its own chunk, loaded on first visit (and preloaded on hover via defaultPreload).
const lazy = <K extends string>(load: () => Promise<Record<K, () => React.ReactNode>>, name: K) => lazyRouteComponent(load, name);
const index = createRoute({
  ...parent,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/pages" });
  },
});
const pages = createRoute({ ...parent, path: "/pages", component: lazy(() => import("./routes/Pages"), "Pages") });
const works = createRoute({ ...parent, path: "/works", component: lazy(() => import("./routes/Works"), "Works") });
const media = createRoute({
  ...parent,
  path: "/media",
  validateSearch: (s: Record<string, unknown>): { id?: string } => (typeof s.id === "string" ? { id: s.id } : {}),
  component: lazy(() => import("./routes/Media"), "Media"),
});
const people = createRoute({ ...parent, path: "/people", component: lazy(() => import("./routes/People"), "People") });
const person = createRoute({ ...parent, path: "/people/$id", component: lazy(() => import("./routes/People"), "PersonEditor") });
const work = createRoute({ ...parent, path: "/works/$id", component: lazy(() => import("./routes/WorkEditor"), "WorkEditor") });
const pageEditor = createRoute({ ...parent, path: "/pages/$slug", component: lazy(() => import("./routes/PageEditor"), "PageEditor") });
const inbox = createRoute({ ...parent, path: "/inbox", component: lazy(() => import("./routes/Inbox"), "Inbox") });
const settings = createRoute({ ...parent, path: "/settings", component: lazy(() => import("./routes/Settings"), "Settings") });

const routeTree = root.addChildren([login, app.addChildren([index, pages, pageEditor, works, work, media, people, person, inbox, settings])]);

export const router = createRouter({ routeTree, context: { queryClient }, defaultPreload: "intent", defaultPreloadStaleTime: 0 });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
